import { z } from "zod";
import { Currency, Measurement } from "@/gql/graphql";
import { relocationFields } from "@/app/Relocations/Validation/relocationValidation";

// Gap rentals must offer at least this fraction off the retail rate.
const MIN_GAP_RENTAL_DISCOUNT = 0.2;

const gapRentalFields = relocationFields.extend({
  distance_mode: z.enum(["UNLIMITED", "PER_UNIT", "FLAT"]),
  distance_value: z.number().min(0).nullish(),
  currency: z.enum(Currency),
  measurement: z.enum(Measurement),
});

// A gap rental sells nothing but extra hire units, so the whole window is the
// ceiling for its minimum hire.
const gapRentalMinimumHireRefine: [
  (schema: {
    extra_hire_units_allowed: number;
    minimum_hire_units: number;
  }) => boolean,
  { message: string; path: PropertyKey[] },
] = [
  (schema) =>
    schema.minimum_hire_units <= (schema.extra_hire_units_allowed ?? 0),
  {
    message: "Cannot be more than the rental length",
    path: ["minimum_hire_units"],
  },
];

const retailRateRefine: [
  (schema: { retail_rate?: number | null }) => boolean,
  { message: string; path: PropertyKey[] },
] = [
  (schema) => Boolean(schema.retail_rate && schema.retail_rate > 0),
  {
    message: "Gap rentals require a retail rate greater than 0",
    path: ["retail_rate"],
  },
];

const sameOfficeRefine: [
  (schema: {
    departureOffice?: { connect: string };
    deliveryOfficeIds?: string[];
  }) => boolean,
  { message: string; path: PropertyKey[] },
] = [
  (schema) =>
    schema.deliveryOfficeIds?.length === 1 &&
    schema.deliveryOfficeIds[0] === schema.departureOffice?.connect,
  {
    message: "Gap rentals must use the same office for pick-up and drop-off",
    path: ["deliveryOfficeIds"],
  },
];

const discountRefine: [
  (schema: { discount_percent?: number | null }) => boolean,
  { message: string; path: PropertyKey[] },
] = [
  (schema) => (schema.discount_percent ?? 0) >= MIN_GAP_RENTAL_DISCOUNT * 100,
  {
    message: `Gap rentals must be at least ${MIN_GAP_RENTAL_DISCOUNT * 100}% off retail`,
    path: ["discount_percent"],
  },
];

const advertisedRateRequiredRefine: [
  (schema: { extra_hire_unit_rate?: number | null }) => boolean,
  { message: string; path: PropertyKey[] },
] = [
  (schema) =>
    Boolean(schema.extra_hire_unit_rate && schema.extra_hire_unit_rate > 0),
  {
    message: "Gap rentals require an advertised rate greater than 0",
    path: ["extra_hire_unit_rate"],
  },
];

const advertisedRateRefine: [
  (schema: {
    retail_rate?: number | null;
    extra_hire_unit_rate?: number | null;
  }) => boolean,
  { message: string; path: PropertyKey[] },
] = [
  (schema) => {
    if (!schema.retail_rate || !schema.extra_hire_unit_rate) {
      return true;
    }

    // Gap rentals must be discounted at least 20% off retail — reject deals
    // priced too close to the supplier's standard rate.
    return (
      schema.extra_hire_unit_rate <=
      schema.retail_rate * (1 - MIN_GAP_RENTAL_DISCOUNT)
    );
  },
  {
    message: `Advertised rate must be at least ${MIN_GAP_RENTAL_DISCOUNT * 100}% below the retail rate`,
    path: ["extra_hire_unit_rate"],
  },
];

const distanceRefine: [
  (schema: {
    distance_mode?: string;
    distance_value?: number | null;
  }) => boolean,
  { message: string; path: PropertyKey[] },
] = [
  (schema) => {
    if (schema.distance_mode === "UNLIMITED") {
      return true;
    }

    // Zero is a real allowance — gap rentals can include no distance and sell
    // distance packs on top.
    return schema.distance_value != null;
  },
  {
    message: "Enter a distance allowance, or set the distance to unlimited",
    path: ["distance_value"],
  },
];

export const gapRentalConfigurationValidation = gapRentalFields.pick({
  currency: true,
  measurement: true,
  hire_unit_type: true,
});

export const gapRentalDetailsValidation = gapRentalFields
  .pick({
    available_from_date: true,
    available_to_date: true,
    departureOffice: true,
    deliveryOfficeIds: true,
    supplier: true,
    vehicle: true,
    supplierInsuranceOption: true,
    quantity: true,
    retail_rate: true,
    discount_percent: true,
    hire_unit_type: true,
    hire_units_allowed: true,
    minimum_hire_units: true,
    extra_hire_units_allowed: true,
    extra_hire_unit_rate: true,
    distance_mode: true,
    distance_value: true,
    inclusions: true,
  })
  .refine(...gapRentalMinimumHireRefine)
  .refine(...retailRateRefine)
  .refine(...discountRefine)
  .refine(...sameOfficeRefine)
  .refine(...advertisedRateRequiredRefine)
  .refine(...advertisedRateRefine)
  .refine(...distanceRefine);

export const gapRentalValidation = gapRentalFields
  .refine(...gapRentalMinimumHireRefine)
  .refine(...retailRateRefine)
  .refine(...discountRefine)
  .refine(...sameOfficeRefine)
  .refine(...advertisedRateRequiredRefine)
  .refine(...advertisedRateRefine)
  .refine(...distanceRefine);
